import * as React from 'react';

import { Select } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';

import { formatAddressDisplay } from './helpers/formatAddress';

import { SupplierAccountRecord } from '~/modules/trade/_graphql/querySupplierAccountsByIds';
import { $updatePurchaseOrder } from '~/modules/trade/_state/purchaseOrderDetail.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { PurchaseOrderRecord } from '~/modules/trade/types/purchaseOrder';

type Props = {
  purchaseOrder: PurchaseOrderRecord;
  afterSubmit?: () => Promise<void>;
  disabled?: boolean;
  selectedAccount: SupplierAccountRecord;
};

export const PurchaseOrderTabSupplierInfoLocationForm: React.FC<Props> = ({
  purchaseOrder,
  afterSubmit,
  disabled = false,
  selectedAccount,
}) => {
  const t = useTranslations();

  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedAddressId, setSelectedAddressId] = React.useState<
    string | null
  >(purchaseOrder.accountAddress?.id ?? null);
  const [initialAddressId, setInitialAddressId] =
    React.useState(selectedAddressId);

  const updatePurchaseOrderMutation = useSetAtom($updatePurchaseOrder);

  const toast = useToast();

  const selectedAccountAddresses = React.useMemo(
    () => selectedAccount?.addresses ?? [],
    [selectedAccount?.addresses],
  );

  const addressOptions = React.useMemo(
    () =>
      selectedAccountAddresses?.map((address) => ({
        key: address.id,
        label: `${address.address.addressType}・${formatAddressDisplay(address.address)}`,
      })) ?? [],
    [selectedAccountAddresses],
  );

  const handleAddressChange = React.useCallback((addressId: string) => {
    setSelectedAddressId(addressId);
  }, []);

  const saveFormData = React.useCallback(
    async (addressId: string | null) => {
      if (!addressId) return;

      setIsSaving(true);
      try {
        await updatePurchaseOrderMutation({
          id: purchaseOrder.id,
          accountAddressId: addressId,
        });

        setInitialAddressId(addressId);

        toast.success(t.purchaseOrderDetail.messages.locationUpdated);

        if (afterSubmit) {
          await afterSubmit();
        }
      } catch (error) {
        console.error('Failed to save location info:', error);
        toast.error(t.purchaseOrderDetail.messages.locationUpdateError);
      } finally {
        setIsSaving(false);
      }
    },
    [purchaseOrder.id, updatePurchaseOrderMutation, toast, t, afterSubmit],
  );

  React.useEffect(() => {
    const isDirty = selectedAddressId !== initialAddressId;

    if (isDirty && selectedAddressId) {
      const timeoutId = setTimeout(() => {
        saveFormData(selectedAddressId);
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedAddressId, initialAddressId, saveFormData]);

  return (
    <div className="@PurchaseOrderTabSupplierInfoLocationForm space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Select
          label={t.purchaseOrdersView.wizard.contractInfo.accountAddress}
          options={addressOptions}
          value={selectedAddressId}
          onChange={handleAddressChange}
          placeholder={
            t.purchaseOrdersView.wizard.placeholders.selectAccountAddress
          }
          isRequired
          isDisabled={isSaving || disabled}
        />
      </div>

      {isSaving && (
        <div className="text-sm text-neutral/80">{t.common.saving}</div>
      )}
    </div>
  );
};
